import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { TUTORIAL_SLIDES } from "../lib/tutorialSlides";
import { Play, Pause, ChevronLeft, ChevronRight, X, RotateCcw, Sparkles } from "lucide-react";

export default function TutorialVideo({ open, onOpenChange }) {
  const slides = TUTORIAL_SLIDES;
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timer = useRef(null);

  const total = slides.length;
  const slide = slides[i];
  const isLast = i === total - 1;

  const totalMins = useMemo(() => {
    const s = slides.reduce((a, x) => a + (x.duration || 16), 0);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  }, [slides]);

  useEffect(() => { if (open) { setI(0); setPlaying(true); } }, [open]);

  useEffect(() => {
    if (!open || !playing) return;
    clearTimeout(timer.current);
    if (isLast) { setPlaying(false); return; }
    timer.current = setTimeout(() => setI((p) => Math.min(p + 1, total - 1)), (slide.duration || 16) * 1000);
    return () => clearTimeout(timer.current);
  }, [open, playing, i, isLast, slide, total]);

  const go = (n) => setI(Math.max(0, Math.min(n, total - 1)));
  const restart = () => { setI(0); setPlaying(true); };

  if (!slide) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 gap-0 overflow-hidden rounded-none border-[#EBE5E0] bg-[#FAF8F5] max-w-3xl w-[94vw]"
        data-testid="tutorial-video">
        <DialogTitle className="sr-only">WorkRoom tutorial</DialogTitle>
        <DialogDescription className="sr-only">An auto-playing walkthrough of WorkRoom's features for designers.</DialogDescription>
        {/* stage */}
        <div className="relative bg-[#FAF8F5] overflow-hidden" style={{ minHeight: "26rem" }}>
          {/* subtle brand motif */}
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(120% 80% at 100% 0%, rgba(166,138,107,0.10), transparent 60%)" }} />
          <span aria-hidden className="absolute -top-6 right-6 font-serif text-[10rem] leading-none text-[#A68A6B]/10 select-none">
            {String(i + 1).padStart(2, "0")}
          </span>

          {/* top bar */}
          <div className="relative flex items-center justify-between px-6 sm:px-10 pt-6">
            <span className="text-[11px] uppercase tracking-[0.28em] text-[#A68A6B] font-serif">WorkRoom · Tour</span>
            <div className="flex items-center gap-3">
              <span className="text-[11px] uppercase tracking-wider text-[#9b938b]">{i + 1} / {total} · {totalMins}</span>
              <button data-testid="tutorial-close" onClick={() => onOpenChange(false)}
                className="text-[#5C5651] hover:text-[#2B2622] transition-colors"><X size={18} /></button>
            </div>
          </div>

          {/* caption */}
          <div key={slide.key} className="relative px-6 sm:px-10 py-10 flex items-center" style={{ minHeight: "18rem" }}
            data-testid="tutorial-slide">
            <div className="max-w-2xl border-l-2 border-[#A68A6B] pl-5 sm:pl-7" style={{ animation: "tutFade 700ms ease-out both" }}>
              <div className="flex items-center gap-2 mb-3">
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#8C7154]">{slide.eyebrow}</p>
                {slide.highlight && (
                  <span className="inline-flex items-center gap-1 bg-[#A68A6B] text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5">
                    <Sparkles size={10} /> Signature
                  </span>
                )}
              </div>
              <h3 className="font-serif text-3xl sm:text-4xl text-[#2B2622] leading-tight mb-4">{slide.title}</h3>
              <p className="text-sm sm:text-base text-[#5C5651] leading-relaxed">{slide.body}</p>
            </div>
          </div>

          {/* progress bar */}
          <div className="absolute bottom-0 inset-x-0 h-1 bg-[#EBE5E0]">
            <div key={`${i}-${playing}`} className="h-full bg-[#A68A6B]"
              style={playing && !isLast
                ? { animation: `tutProgress ${(slide.duration || 16)}s linear forwards` }
                : { width: isLast ? "100%" : "0%" }} />
          </div>
        </div>

        {/* controls */}
        <div className="flex items-center justify-between gap-4 bg-white border-t border-[#EBE5E0] px-5 py-3">
          <div className="flex items-center gap-2">
            <button data-testid="tutorial-prev" onClick={() => go(i - 1)} disabled={i === 0}
              className="p-1.5 text-[#5C5651] hover:text-[#A68A6B] disabled:opacity-30 transition-colors"><ChevronLeft size={20} /></button>
            {isLast ? (
              <button data-testid="tutorial-restart" onClick={restart}
                className="p-1.5 text-[#5C5651] hover:text-[#A68A6B] transition-colors"><RotateCcw size={18} /></button>
            ) : (
              <button data-testid="tutorial-playpause" onClick={() => setPlaying((p) => !p)}
                className="p-1.5 text-[#5C5651] hover:text-[#A68A6B] transition-colors">
                {playing ? <Pause size={18} /> : <Play size={18} />}
              </button>
            )}
            <button data-testid="tutorial-next" onClick={() => go(i + 1)} disabled={isLast}
              className="p-1.5 text-[#5C5651] hover:text-[#A68A6B] disabled:opacity-30 transition-colors"><ChevronRight size={20} /></button>
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto">
            {slides.map((s, idx) => (
              <button key={s.key} data-testid={`tutorial-dot-${idx}`} onClick={() => go(idx)} title={s.title}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-[#A68A6B]" : "w-1.5 bg-[#D9CBBF] hover:bg-[#A68A6B]"}`} />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
